import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";

const RANGE = "standings_pro!A1:AI100"; // wir zeigen A..AD an, brauchen aber AG..AI für die Bild-URLs

const colIndex = (letter) => {
    let n = 0;
    for (let i = 0; i < letter.length; i++) n = n * 26 + (letter.charCodeAt(i) - 64);
    return n;
};

const LETTER_AD = colIndex("AD"); // 30 (letzte sichtbare Spalte)
//const LETTER_AG = colIndex("AG"); // 33 (Flag-URL)
//const LETTER_AH = colIndex("AH"); // 34 (Car-URL)
//const LETTER_AI = colIndex("AI"); // 35 (Team-URL)
const LETTER_P  = colIndex("P");  // 16 (R1)
const LETTER_AA = colIndex("AA"); // 27 (R12)

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function computeTop3ByColumn(rowsAsArrays, startCol, endCol) {
    const map = new Map();
    for (let c = startCol; c <= endCol; c++) {
        const nums = rowsAsArrays
            .map(arr => arr[c - 1])
            .filter(v => v !== null && v !== undefined && v !== "")
            .map(v => Number(v))
            .filter(v => !Number.isNaN(v));
        if (!nums.length) { map.set(c, null); continue; }

        const uniqDesc = Array.from(new Set(nums)).sort((a, b) => b - a);
        map.set(c, {
            gold:   uniqDesc[0] ?? null,
            silver: uniqDesc[1] ?? null,
            bronze: uniqDesc[2] ?? null,
        });
    }
    return map;
}

export default function StandingsPro() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({ range: RANGE });
                setData(res);
            } catch (e) {
                const msg = e?.message || String(e);
                // Sonderfall: Range existiert noch nicht -> freundliche Empty-Message
                if (msg.includes("Unable to parse range")) {
                    setNoData(true);
                } else {
                    setError(msg);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { visibleHeaders, indices, rowsAsArrays, top3PerRaceColumn, completedRuns } = useMemo(() => {
        const headers = data.header || [];
        const visibleHeaders = headers.slice(0, LETTER_AD);

        const idxAG = colIndex("AG") - 1; // Flag URL
        const idxAH = colIndex("AH") - 1; // Car  URL
        const idxAI = colIndex("AI") - 1; // Team URL

        const rowsAsArrays = (data.rows || []).map((r) => headers.map((h) => r[h]));
        const top3PerRaceColumn = computeTop3ByColumn(rowsAsArrays, LETTER_P, LETTER_AA);

        // === NEU: gefahrene Läufe zählen (eine Spalte zählt als "gefahren",
        // wenn irgendeine Zeile dort eine Zahl hat)
        let completedRuns = 0;
        for (let c = LETTER_P; c <= LETTER_AA; c++) {
            const anyNumberInCol = rowsAsArrays.some(row => {
                const v = row[c - 1];
                return v !== null && v !== "" && !Number.isNaN(Number(v));
            });
            if (anyNumberInCol) completedRuns++;
        }

        return {
            visibleHeaders,
            indices: { idxAG, idxAH, idxAI },
            rowsAsArrays,
            top3PerRaceColumn,
            completedRuns, // <<— zurückgeben
        };
    }, [data]);

    if (loading) return <div className="card"><p>Lade Standings...</p></div>;
    if (noData) {
        return (
            <div className="card">
                <h2>Standings – PRO</h2>
                <p className="muted">
                    Aktuell sind noch keine Rennergebnisse im System.
                    Sobald das erste Rennen gefahren ist, erscheinen hier die PRO-Standings der GT3 Xperience.
                </p>
            </div>
        );
    }
    if (error)   return <div className="card"><p style={{ color:"#f55" }}>Fehler: {error}</p></div>;

    return (
        <>
            <div className="card">
                <h2>Standings – PRO</h2>
                <p className="muted">
                    Pro Wertung nach Abschluss des {completedRuns}. Laufes präsentiert von BavarianSimTec. In der GTX gibt es keine Participation Points.
                </p>
            </div>

            <div className="fancyTable">
                <table className="tbl">
                    <thead>
                    <tr>
                        {visibleHeaders.map((h, i) => {
                            const colNumber = i + 1;

                            // Kopfzeilen-Anpassungen:
                            // D = Fahrzeugnummer -> "#"
                            const label =
                                colNumber === 1 ? "Pos" :
                                    colNumber === 4 ? "#"   :
                                        h;

                            const isRace    = colNumber >= LETTER_P && colNumber <= LETTER_AA;
                            const raceLeft  = colNumber === LETTER_P;
                            const raceRight = colNumber === LETTER_AA;
                            const evenStripe = isRace && ((colNumber - LETTER_P) % 2 === 0);

                            // Linksbündig nur A (Pos) und F (Fahrername)
                            const leftAligned = (colNumber === 1 || colNumber === 6);

                            const thClass = [
                                isRace ? "race-col" : "",
                                raceLeft ? "race-left" : "",
                                raceRight ? "race-right" : "",
                                evenStripe ? "race-alt" : "",
                                // Bildspalten: C (Flag), E (Car), G (Teamlogo)
                                (colNumber === 3 || colNumber === 5 || colNumber === 7) ? "icon" : "",
                                leftAligned ? "align-left" : "",
                            ].filter(Boolean).join(" ");

                            return <th key={i} className={thClass}>{label}</th>;
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {rowsAsArrays.map((arr, ri) => (
                        <tr key={ri}>
                            {visibleHeaders.map((_, i) => {
                                const colNumber = i + 1;
                                const val = arr[i];

                                // Bilder laut deiner Spezifikation:
                                // C: Flag (aus AG), E: Car (aus AH), G: Teamlogo (aus AI)
                                if (colNumber === 3) {
                                    const src = arr[indices.idxAG] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Flag" className="flag lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }
                                if (colNumber === 5) {
                                    const src = arr[indices.idxAH] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Car" className="carlogo lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }
                                if (colNumber === 7) {
                                    const src = arr[indices.idxAI] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Team" className="teamlogo lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }

                                // Rennspalten P..AA mit Top-3-Highlight
                                const isRace    = colNumber >= LETTER_P && colNumber <= LETTER_AA;
                                const raceLeft  = colNumber === LETTER_P;
                                const raceRight = colNumber === LETTER_AA;
                                const evenStripe = isRace && ((colNumber - LETTER_P) % 2 === 0);

                                let tierClass = "";
                                if (isRace && val !== "" && val !== null && val !== undefined) {
                                    const numVal = Number(val);
                                    const t3 = top3PerRaceColumn.get(colNumber);
                                    if (t3 && !Number.isNaN(numVal)) {
                                        if (numVal === t3.gold)        tierClass = "race-gold";
                                        else if (numVal === t3.silver) tierClass = "race-silver";
                                        else if (numVal === t3.bronze) tierClass = "race-bronze";
                                    }
                                }

                                // Linksbündig nur A (Pos) und F (Fahrername)
                                const leftAligned = (colNumber === 1 || colNumber === 6);

                                // Delta Pos (B) einfärben
                                let deltaClass = "";
                                if (colNumber === 2) {
                                    const n = Number(val);
                                    if (!isNaN(n)) {
                                        if (n > 0) deltaClass = "delta-up";
                                        else if (n < 0) deltaClass = "delta-down";
                                    }
                                }

                                // Kompakte Metriken (Diff..DNFs = J..O => 10..15)
                                const compactMetrics = (colNumber >= 10 && colNumber <= 15) ? "compact-metrics" : "";

                                const numeric =
                                    isNumeric(val) ||
                                    (colNumber >= 9 && colNumber <= 15) || // Points..DNFs
                                    isRace;

                                const tdClass = [
                                    numeric ? "num" : "",
                                    isRace ? "race-col" : "",
                                    raceLeft ? "race-left" : "",
                                    raceRight ? "race-right" : "",
                                    evenStripe ? "race-alt" : "",
                                    tierClass,
                                    leftAligned ? "align-left" : "",
                                    (colNumber === 9) ? "points-highlight" : "", // Points (I)
                                    (colNumber === 1) ? "pos-highlight" : "",    // Pos (A)
                                    deltaClass,
                                    compactMetrics,
                                ].filter(Boolean).join(" ");

                                return <td key={i} className={tdClass}>{val}</td>;
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
